import { CloseOutlined } from "@ant-design/icons";
import { Alert, Button, Drawer } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDeleteProject } from "../../../api";
import { Project } from "../../../api/models";
import { DeleteButton } from "../../../components/DeleteButton";
import { EditableText } from "../../../util/EditableText";
import { useNodeSelectedContext } from "../../../contexts/NodeSelectedContext";
import "../drawer.scss";
import { useUpdateConstructionProjectInformation } from "../useUpdateInformation";
import { SitesDetails } from "./SitesDetails";
import { SummaryInformation } from "./SummaryInformation";

interface ConstructionProjectDrawerProps {
  constructionProject: Project | undefined;
  drawerVisible: boolean;
  onSave: (refreshTimeStamp: string) => void;
}

export function ConstructionProjectDrawer({
  constructionProject,
  drawerVisible,
  onSave,
}: ConstructionProjectDrawerProps) {
  const { t } = useTranslation();

  useEffect(() => {
    if (!drawerVisible) {
      resetUpdateInfo();
    }
  }, [drawerVisible]);

  const {
    updateResult,
    updateProjectCity,
    updateProjectName,
    resetUpdateInfo,
  } = useUpdateConstructionProjectInformation(constructionProject?.id, onSave);

  const deleteProject = useDeleteProject(constructionProject?.id || "");

  const { setSelectedNode } = useNodeSelectedContext();

  const showSitesDrawer = (
    siteId: string,
    projectName: string,
    projectId: string,
    orgId: string
  ) => {
    setSelectedNode({
      selectedSiteId: siteId,
      selectedProjectId: projectId,
      selectedProjectName: projectName,
      selectedOrgId: orgId,
      siteDrawerVisible: true,
      projectDrawerVisible: false,
    });
  };

  return (
    <Drawer
      title={
        <div data-testid="construction-project-drawer">
          <div className="title-container">
            <EditableText
              text={constructionProject?.name}
              style={{ width: "450px" }}
              saveCallback={(value) => {
                updateProjectName(value);
              }}
            />
            <Button
              type="text"
              className="close-card-drawer-button"
              icon={<CloseOutlined />}
              onClick={() => setSelectedNode({ projectDrawerVisible: false })}
            />
          </div>
        </div>
      }
      placement="right"
      closable={false}
      onClose={() => setSelectedNode({ projectDrawerVisible: false })}
      open={drawerVisible}
      width={600}
    >
      {updateResult?.status === 400 &&
        updateResult.updatedValue.key === "projectName" && (
          <Alert
            message={t("construction-project:update-project-name-failed")}
            type="error"
            closable
          />
        )}
      {updateResult?.status === 200 &&
        updateResult.updatedValue.key === "projectName" && (
          <Alert
            message={t("construction-project:update-project-name-success")}
            type="success"
            closable
          />
        )}
      <SummaryInformation
        location={constructionProject?.address?.city || ""}
        organizationName={constructionProject?.org.name || ""}
        organizationId={constructionProject?.org.id || ""}
        updateResult={updateResult}
        onSave={updateProjectCity}
      />
      <SitesDetails
        constructionProject={constructionProject}
        handleSiteClicked={(siteId, projectName, projectId, orgId) => {
          showSitesDrawer(siteId, projectName, projectId, orgId);
        }}
      />
      <DeleteButton
        name={constructionProject?.name}
        onDelete={deleteProject}
        refresh={() => {
          onSave(new Date().toISOString());
          drawerVisible = false;
          setSelectedNode({ projectDrawerVisible: false });
        }}
      />
    </Drawer>
  );
}
